import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { AlertTriangle, CheckCircle, Database, Loader, RefreshCw, Trash2 } from 'lucide-react';

const ClearSeedData = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState({ success: [], errors: [] });
  const [dataStatus, setDataStatus] = useState(null);

  const collectionsToClear = [
    { name: COLLECTIONS.CATEGORIES, label: 'الفئات' },
    { name: COLLECTIONS.PRODUCTS, label: 'المنتجات' },
    { name: COLLECTIONS.SERVICES, label: 'الخدمات' },
    { name: COLLECTIONS.MODIFICATIONS, label: 'التعديلات' },
  ];

  const checkExistingData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const snapshots = await Promise.all(
        collectionsToClear.map(({ name }) => getDocs(collection(db, name)))
      );

      const status = snapshots.reduce((acc, snapshot, idx) => {
        const key = collectionsToClear[idx].name;
        acc[key] = snapshot.size;
        return acc;
      }, {});

      const heroDoc = await getDoc(doc(db, 'settings', 'hero'));
      status.hero = heroDoc.exists() ? 1 : 0;

      setDataStatus(status);
    } catch (error) {
      console.error('Error checking data status:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    checkExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSeedData = async () => {
    const confirmed = window.confirm('هل أنت متأكد من أنك تريد حذف جميع البيانات التجريبية؟ سيتم حذف الفئات، المنتجات، الخدمات، التعديلات، وإعدادات الـ Hero.');
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setProgress('جاري حذف البيانات التجريبية...');
    setResults({ success: [], errors: [] });

    const successList = [];
    const errorList = [];

    try {
      for (const { name, label } of collectionsToClear) {
        setProgress(`جاري حذف ${label}...`);
        try {
          const snapshot = await getDocs(collection(db, name));
          if (snapshot.empty) {
            successList.push(`لا توجد ${label.toLowerCase()} لحذفها.`);
            continue;
          }

          await Promise.all(snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
          successList.push(`✅ تم حذف ${snapshot.size} من ${label}.`);
        } catch (error) {
          console.error(`Error clearing collection ${name}:`, error);
          errorList.push(`❌ خطأ أثناء حذف ${label}: ${error.message}`);
        }
      }

      setProgress('جاري حذف إعدادات الـ Hero...');
      try {
        const heroRef = doc(db, 'settings', 'hero');
        const heroSnapshot = await getDoc(heroRef);
        if (heroSnapshot.exists()) {
          await deleteDoc(heroRef);
          successList.push('✅ تم حذف إعدادات الـ Hero.');
        } else {
          successList.push('إعدادات الـ Hero غير موجودة.');
        }
      } catch (error) {
        console.error('Error deleting hero settings:', error);
        errorList.push(`❌ خطأ أثناء حذف إعدادات الـ Hero: ${error.message}`);
      }

      if (errorList.length === 0) {
        setProgress('✅ تم حذف جميع البيانات التجريبية.');
      } else {
        setProgress('⚠️ تم إكمال الحذف مع بعض الأخطاء.');
      }

      setResults({ success: successList, errors: errorList });
      await checkExistingData(true);
    } catch (error) {
      console.error('Error in clearSeedData:', error);
      errorList.push(`❌ خطأ عام: ${error.message}`);
      setResults({ success: successList, errors: errorList });
      setProgress('حدث خطأ أثناء عملية الحذف.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">حذف البيانات التجريبية</h1>
        <p className="text-gray-600 mt-2">
          احذف جميع البيانات التي تمت إضافتها بواسطة ميزة البيانات التجريبية بضغطه واحدة.
        </p>
      </div>

      <div className="bg-red-50 border-r-4 border-red-500 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium mb-1">تحذير:</p>
            <p className="text-red-700 text-sm mb-2">
              هذه العملية ستقوم بحذف كل الفئات، المنتجات، الخدمات، التعديلات، وإعدادات الـ Hero في قاعدة البيانات.
            </p>
            <p className="text-red-600 text-xs">
              لا يمكن التراجع عن هذه العملية، تأكد من أخذ نسخة احتياطية إذا كانت البيانات مهمة.
            </p>
          </div>
        </div>
      </div>

      {dataStatus && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">البيانات الحالية في Firebase:</h2>
            <button
              onClick={() => checkExistingData()}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCw className="w-4 h-4" />
              تحديث
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {collectionsToClear.map(({ name, label }) => (
              <div key={name} className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">{label}</p>
                <p className="text-2xl font-bold text-gray-800">{dataStatus[name] ?? 0}</p>
              </div>
            ))}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">إعدادات الـ Hero</p>
              <p className="text-2xl font-bold text-gray-800">{dataStatus.hero || 0}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-4 mb-6">
          <Database className="w-8 h-8 text-red-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">البيانات التي سيتم حذفها:</h2>
            <ul className="text-sm text-gray-600 mt-2 space-y-1">
              <li>• الفئات ({dataStatus?.categories ?? 0})</li>
              <li>• المنتجات ({dataStatus?.products ?? 0})</li>
              <li>• الخدمات ({dataStatus?.services ?? 0})</li>
              <li>• التعديلات ({dataStatus?.modifications ?? 0})</li>
              <li>• إعدادات الـ Hero ({dataStatus?.hero ?? 0})</li>
            </ul>
          </div>
        </div>

        <button
          onClick={clearSeedData}
          disabled={loading}
          className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              {progress}
            </>
          ) : (
            <>
              <Trash2 className="w-5 h-5" />
              حذف البيانات التجريبية
            </>
          )}
        </button>

        {progress && !loading && (
          <div className="text-center mt-4">
            <p className="text-gray-700 font-medium mb-2">{progress}</p>
          </div>
        )}
      </div>

      {(results.success.length > 0 || results.errors.length > 0) && (
        <div className="space-y-4">
          {results.success.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-green-900">
                  تم بنجاح ({results.success.length})
                </h3>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {results.success.map((msg, idx) => (
                  <p key={idx} className="text-sm text-green-700">
                    ✓ {msg}
                  </p>
                ))}
              </div>
            </div>
          )}

          {results.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-red-900">
                  أخطاء ({results.errors.length})
                </h3>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {results.errors.map((msg, idx) => (
                  <p key={idx} className="text-sm text-red-700">
                    ✗ {msg}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClearSeedData;
